/**
 * UImgSelectorMode — mode sélecteur modal de UImg (ha/447).
 *
 * Détaché de UImg pour clarifier la responsabilité : décider du sélecteur
 * (DSelector pour URL, EditorMultiMedia pour fichier), monter la FSApp,
 * router le callback `itemClick` vers l'update src / persistence appropriée.
 *
 * Hardening ha/447 :
 *  - guard `data[0].getFile()` (data peut être absent/non-array selon le sélecteur).
 *  - `formId` append désormais conditionnel (uniformisé avec `uploadFile`).
 *  - `udata` absent → fallback Map vide (au lieu de crasher `udata.set`).
 *  - `dst` revalidé via `sd.get("dst")` à chaque ouverture (loadSelector neuf si sd change).
 */

import { toMap, getAppService, getDataRepository, log } from "../../../../HA";
import { DSelector } from "../Editor/DSelector";
import { EditorMultiMedia } from "../Editor/MultiMedia";
import { App as ServiceApp } from "../../Service/App";
import type { FSApp } from "../../Entity/FSApp";
import type { Img } from "../Img";

export interface UImgSelectorHost {
    get(key: string): any;
    has(key: string): boolean;
    addToData(key: string, value: any): unknown;
    translate(value: any): any;
    translateAll(value: any): Map<any, any>;
    getImg(): Img;
    getImageSrc(): string;
    getUploadFieldName(): string;
}

export class UImgSelectorMode {
    constructor(private readonly host: UImgSelectorHost) {}

    public openModal(): void {
        const app2 = ServiceApp.createFSApp(this.loadSelector());
        this.setAppId(app2.getId());
        this.showModal(app2);
    }

    private loadSelector(): DSelector | EditorMultiMedia {
        // Hardening ha/447 : cache invalidé si `sd` a changé d'identité depuis
        // la dernière construction. Sinon `dst`/`selector` figés au premier
        // appel — un nouveau sd injecté entre deux handleClick serait ignoré.
        const sdRaw = this.host.get("sd");
        if (this.host.has("selector") && this.host.get("_selectorSdRef") === sdRaw) {
            return this.host.get("selector");
        }

        const sd = this.host.translateAll(toMap(sdRaw));
        const dst = sd.get("dst");
        this.host.addToData("dst", dst === "url" ? "url" : "file");
        const selector = dst === "url" ? this.createDSelector(sd) : this.createMediaSelector(sd);
        this.host.addToData("_selectorSdRef", sdRaw);
        return selector;
    }

    private createDSelector(sd: Map<any, any>): DSelector {
        const selector = new DSelector(toMap({
            pfunc: {
                itemClick: (d: any) => this.itemClicked(d),
            },
            sd,
            waccept: () => this.closeModal(),
            wcancel: () => this.closeModal(),
        }));
        this.host.addToData("selector", selector);
        return selector;
    }

    private createMediaSelector(sd: Map<any, any>): EditorMultiMedia {
        const selector = new EditorMultiMedia(toMap({
            max: 1,
            pfunc: {
                itemClick: (d: any) => this.itemClicked(d),
            },
            sd,
            t: "img",
            tc: 1,
            waccept: () => this.closeModal(),
            wcancel: () => this.closeModal(),
        }));
        this.host.addToData("selector", selector);
        return selector;
    }

    private itemClicked(data: any): void {
        this.closeModal();

        if (this.host.get("dst") === "url") {
            this.host.addToData("mid", data);
            // Guard ha/447 : `udata` peut être absent → translateAll(undefined)
            // renvoie une Map vide, on travaille dessus sans crasher.
            const udata = this.host.translateAll(this.host.get("udata") ?? {});
            udata.set("mid", data);
            this.host.getImg().updateSrc(this.host.getImageSrc());
            getDataRepository().saveData(udata).then((_response: Map<any, any>) => {
                log("enrégistré!");
            });
            return;
        }

        // Guard ha/447 : data peut être absent/non-array selon le sélecteur ;
        // sortir proprement plutôt que crasher `data[0].getFile()`.
        if (!Array.isArray(data) || data.length === 0 || !data[0] || typeof data[0].getFile !== "function") {
            log(["UImg itemClicked: payload sélecteur invalide", data]);
            return;
        }

        const f: File = data[0].getFile();
        this.host.getImg().updateSrc(data[0].getFileData());
        const form = new FormData();
        form.append(this.host.getUploadFieldName(), f);
        // Guard ha/447 : formId append conditionnel (uniformisé avec uploadFile).
        if (this.host.get("formid")) {
            form.append("formId", this.host.get("formid"));
        }

        getDataRepository().saveForm(form).then((response: Map<any, any>) => {
            getAppService().processAction(response);
        }).catch((reason: any) => {
            log(["media upload error", reason]);
        });
    }

    private closeModal(): void {
        getAppService().closeFSApp(this.getAppId());
    }

    private showModal(app: FSApp): void {
        getAppService().showFSApp(app);
    }

    private setAppId(id: string) {
        return this.host.addToData("_appId", id);
    }

    private getAppId(): string {
        return this.host.get("_appId");
    }
}
